// import { Divider } from 'antd'
// import dayjs from 'dayjs'
import Cookies from 'js-cookie'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, NavLink } from 'react-router-dom'

import DiscordIcon from '@/assets/icons/discord.svg?react'
import DocumentationIcon from '@/assets/icons/documentation.svg?react'
import EyeClose from '@/assets/icons/eye-close.svg?react'
import EyeOpen from '@/assets/icons/eye-open.svg?react'
import OnboardingGuideIcon from '@/assets/icons/onboarding-guide.svg?react'
import TeamLogoImage from '@/assets/images/team-logo.svg?react'
import { message, Trans } from '@/components'
import { Button } from '@/components/base'
import { BrandLogo } from '@/components/BrandLogo'
// import { Trans } from '@/components/Trans.tsx'
import { TeamRole } from '@/consts/team'
import { useUnifiedContext } from '@/contexts/UnifiedContext'
import { useBillingAnnouncement } from '@/hooks/useBillingAnnouncement'
import { getBonusStat } from '@/models/credit'
import { EduVerify } from '@/models/profile.ts'
import { startOAuthBindFollow } from '@/pages/profile/utils/social-media'
import { useTeamUserRole } from '@/stores/team'
import { useUserStore } from '@/stores/user'
import { formatDurationToHours, formatPrice } from '@/utils/formatter.ts'

import { AnnouncementBanner } from './AnnouncementBanner'
import { CreditBreakdown } from './CreditBreakdown'
import { OnboardingGuideModal } from './onboarding/OnboardingGuideModal'
import { useOnboardingTasks } from './onboarding/useOnboardingTasks'
import { UserDropdown } from './UserDropdown'

interface HeaderProps {
  setExchargeModalOpen: (open: boolean) => void
  eduVerify?: EduVerify
}

const HEADER_DOC_LINKS = [
  {
    label: 'API Doc',
    hrefByLanguage: {
      en: 'https://sdkdoc.glows.ai/en',
      ja: 'https://sdkdoc.glows.ai/ja',
    },
  },
  {
    label: 'User Doc',
    hrefByLanguage: {
      en: 'https://docs.glows.ai/en',
      ja: 'https://docs.glows.ai/ja',
    },
  },
] as const

export const Header = ({ setExchargeModalOpen, eduVerify }: HeaderProps) => {
  const { i18n, t } = useTranslation()
  const account = useUserStore((state) => state.account)
  const bonusStat = getBonusStat(account)
  const [isBalanceHidden, setIsBalanceHidden] = useState(false)
  const [showDiscordTooltip, setShowDiscordTooltip] = useState(false)
  const [onboardingOpen, setOnboardingOpen] = useState(false)
  const [followXConnecting, setFollowXConnecting] = useState(false)
  const [showDocsMenu, setShowDocsMenu] = useState(false)
  const docsMenuTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const brandRef = useRef<HTMLDivElement>(null)
  const actionsRef = useRef<HTMLDivElement>(null)
  const announcementRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const brand = brandRef.current
    const actions = actionsRef.current
    const announcement = announcementRef.current
    if (!brand || !actions || !announcement) return

    const updateAnnouncementWidth = () => {
      // Reserve equal space on both sides to keep the announcement at the viewport center.
      const sideWidth = Math.max(
        brand.getBoundingClientRect().width,
        actions.getBoundingClientRect().width,
      )
      announcement.style.maxWidth = `calc(100% - ${(sideWidth + 32) * 2}px)`
      announcement.style.setProperty(
        '--announcement-row-height',
        `${Math.max(40, brand.offsetHeight, actions.offsetHeight)}px`,
      )
    }

    updateAnnouncementWidth()
    const observer = new ResizeObserver(updateAnnouncementWidth)
    observer.observe(brand)
    observer.observe(actions)
    return () => observer.disconnect()
  }, [])

  // Use unified context for platform/team mode detection
  const { mode } = useUnifiedContext()
  const { personalMode, teamSharedMode } = useBillingAnnouncement()
  const userRole = useTeamUserRole()
  const isTeamContext = mode === 'team'
  const canRecharge = mode === 'platform' || userRole === TeamRole.Owner
  const docLanguage =
    i18n.resolvedLanguage?.startsWith('ja') || i18n.language.startsWith('ja') ? 'ja' : 'en'

  const { tasks: onboardingTasks, loading: onboardingLoading } = useOnboardingTasks(!isTeamContext)
  const onboardingUndoneCount =
    Number(!onboardingTasks.followXDone) + Number(!onboardingTasks.firstDepositDone)
  const shouldShowOnboardingGuide =
    !isTeamContext && !onboardingLoading && onboardingUndoneCount > 0

  const toggleBalanceVisibility = () => {
    setIsBalanceHidden(!isBalanceHidden)
  }

  const handleDocsMenuMouseEnter = () => {
    if (docsMenuTimeoutRef.current) {
      clearTimeout(docsMenuTimeoutRef.current)
    }
    setShowDocsMenu(true)
  }

  const handleDocsMenuMouseLeave = () => {
    docsMenuTimeoutRef.current = setTimeout(() => {
      setShowDocsMenu(false)
    }, 150)
  }

  const handleFollowXClick = async () => {
    if (followXConnecting) return

    setFollowXConnecting(true)
    try {
      await startOAuthBindFollow('x')
      setOnboardingOpen(false)
    } catch (error) {
      const err = error as Error
      if (err.message === 'POPUP_BLOCKED') {
        message.error(t('profile.socialMedia.connectPopupBlocked'))
      } else if (err.message === 'NOT_LOGGED_IN') {
        message.error(t('profile.socialMedia.connectNotLoggedIn'))
      } else {
        message.error(err.message || t('profile.socialMedia.connectFailed'))
      }
    } finally {
      setFollowXConnecting(false)
    }
  }

  useEffect(() => {
    if (Cookies.get('is_balance_hidden')) {
      setIsBalanceHidden(Cookies.get('is_balance_hidden') === 'true')
    }
  }, [])

  useEffect(() => {
    Cookies.set('is_balance_hidden', String(isBalanceHidden))
  }, [isBalanceHidden])

  useEffect(() => {
    const showTooltip = () => {
      setShowDiscordTooltip(true)
      setTimeout(() => {
        setShowDiscordTooltip(false)
      }, 10000) // Hide after 10 seconds
    }

    // Show immediately
    showTooltip()

    // Set up interval to show every 30 seconds
    const interval = setInterval(showTooltip, 30000)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    return () => {
      if (docsMenuTimeoutRef.current) {
        clearTimeout(docsMenuTimeoutRef.current)
      }
    }
  }, [])

  return (
    <header className="px-4 py-[18px] sticky top-0 z-[50]" data-ai-component="top-bar">
      <div className="flex items-center justify-between gap-4 min-w-[1024px]">
        <div ref={brandRef} className="flex items-center shrink-0 self-start mt-1">
          <Link
            to={isTeamContext ? '/team/home' : '/create'}
            replace
            className="mr-4"
            data-ai-component="brand-logo"
            data-ai-action={isTeamContext ? 'go-to-team-home' : 'go-to-create'}
            data-ai-value={isTeamContext ? '/team/home' : '/create'}
            aria-label="Glows.ai home"
          >
            <BrandLogo width={140} height={32} />
          </Link>
          {isTeamContext && <TeamLogoImage width={50} height={24} />}
        </div>

        <div
          ref={announcementRef}
          className="absolute top-[18px] left-1/2 -translate-x-1/2 w-[620px]"
        >
          <AnnouncementBanner
            personalMode={personalMode}
            teamSharedMode={teamSharedMode}
            isTeamContext={isTeamContext}
          />
        </div>

        <div
          ref={actionsRef}
          className="flex flex-wrap items-center gap-x-4 gap-y-2 shrink-0 justify-end max-w-[calc(50%-64px)]"
        >
          {/*<div className="dropdown group relative">*/}
          {/*  <span className="t-sb px-3 py-2">0 Bit</span>*/}
          {/*  <ul className="w-[340px] absolute -left-1/2 top-full bg-panel-white shadow-card-100 rounded-2xl p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transform transition-all duration-300">*/}
          {/*    <li className="w-full flex items-center justify-between mb-2 py-2">*/}
          {/*      <div className="t-xs text-secondary">My Bits (Bit)</div>*/}
          {/*      <div className="t-sb text-primary">-</div>*/}
          {/*    </li>*/}
          {/*    <Divider className="my-2 bg-neutral-200" />*/}
          {/*    <li className="w-full flex items-center justify-between mb-2 py-2">*/}
          {/*      <div className="t-xs text-secondary">Invitation Code</div>*/}
          {/*      <div className="t-sb text-primary">{userStore.invitationInfo.invitationCode}</div>*/}
          {/*    </li>*/}
          {/*    <li className="w-full flex items-center justify-between mb-2 py-2">*/}
          {/*      <div className="t-xs text-secondary">Successful Referrals</div>*/}
          {/*      <div className="t-sb text-primary">{userStore.invitationInfo.totalInvited}</div>*/}
          {/*    </li>*/}
          {/*    <li className="w-full flex items-center justify-between mb-4 py-2">*/}
          {/*      <div className="t-xs text-secondary">Total Bits Earned (Bit)</div>*/}
          {/*      <div className="t-sb text-primary">-</div>*/}
          {/*    </li>*/}
          {/*    <li className="w-full flex items-center justify-between mb-2">*/}
          {/*      <Button className="w-full t-x-sb py-2" disabled>*/}
          {/*        History*/}
          {/*      </Button>*/}
          {/*    </li>*/}
          {/*    <li className="w-full flex items-center justify-between mb-2">*/}
          {/*      <Button type="secondary" className="w-full t-x-sb py-2" disabled>*/}
          {/*        Exchange*/}
          {/*      </Button>*/}
          {/*    </li>*/}
          {/*  </ul>*/}
          {/*</div>*/}

          <div
            className="dropdown group relative text-nowrap"
            data-ai-component="user-credits"
            data-ai-state={isBalanceHidden ? 'hidden' : 'visible'}
            data-ai-value={((account.totalBonus.moneyCent ?? 0) / 100).toFixed(3)}
          >
            <div className="t-sb py-2 flex space-x-2 items-center">
              <div>
                {isBalanceHidden
                  ? '∗∗∗∗∗∗'
                  : formatPrice(
                      account.totalBonus.moneyCent ?? 0,
                      false,
                      account.totalBonus.moneyCent > 100 ? 'Credits' : 'Credit',
                    )}
              </div>
              <div
                className="cursor-pointer text-secondary"
                onClick={toggleBalanceVisibility}
                data-ai-action={isBalanceHidden ? 'show-credits' : 'hide-credits'}
                aria-label={isBalanceHidden ? 'Show credits' : 'Hide credits'}
              >
                {isBalanceHidden ? (
                  <EyeClose className="w-4 h-4" />
                ) : (
                  <EyeOpen className="w-4 h-4" />
                )}
              </div>
            </div>
            <ul className="w-[280px] absolute -left-1/2 top-full bg-panel-white shadow-card-100 rounded-2xl p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transform transition-all duration-300">
              <li className="w-full flex items-center justify-between mb-2 py-2">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="header.creditInfo.balance">Balance (Credit)</Trans>
                </div>
                <div className="t-sb text-primary">
                  {isBalanceHidden
                    ? '∗∗∗∗∗∗'
                    : formatPrice(account.totalBonus.moneyCent ?? 0, false, '')}
                </div>
              </li>
              {bonusStat && (
                <li className="w-full mb-2">
                  <CreditBreakdown bonusStat={bonusStat} isBalanceHidden={isBalanceHidden} />
                </li>
              )}
              <li className="w-full flex items-center justify-between mb-2 py-2">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="header.creditInfo.totalSpending">Total Spending (Credit)</Trans>
                </div>
                <div className="t-sb text-primary">
                  {isBalanceHidden ? '∗∗∗∗∗∗' : formatPrice(account.expense.amount ?? 0, false, '')}
                </div>
              </li>
              <li className="w-full flex items-center justify-between mb-2 py-2">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="header.creditInfo.duration">Total Duration (h)</Trans>
                </div>
                <div className="t-sb text-primary">
                  {isBalanceHidden ? '∗∗∗∗∗∗' : formatDurationToHours(account.expense.timeSec ?? 0)}
                </div>
              </li>
              <li className="w-full flex items-center justify-between mb-4 py-2">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="header.creditInfo.invoicingLimit">Invoicing Limit (Credit)</Trans>
                </div>
                <div className="t-sb text-primary">-</div>
              </li>
              <li className="w-full flex items-center justify-between mb-2">
                <NavLink to={isTeamContext ? '/team/billing' : '/billing'} className="w-full">
                  <Button
                    className="w-full t-x-sb py-2"
                    data-ai-action="go-to-billing"
                    data-ai-value={isTeamContext ? '/team/billing' : '/billing'}
                  >
                    <Trans i18nKey="header.creditInfo.mySpendingBtn">My Spending</Trans>
                  </Button>
                </NavLink>
              </li>
              {canRecharge && (
                <li className="w-full flex items-center justify-between mb-2">
                  <Button
                    type="secondary"
                    className="w-full t-x-sb py-2"
                    onClick={() => setExchargeModalOpen(true)}
                    data-ai-action="open-recharge"
                  >
                    <Trans i18nKey="header.creditInfo.rechargeBtn">Recharge</Trans>
                  </Button>
                </li>
              )}
            </ul>
          </div>

          <div className="h-5 border-r border-r-gray100"></div>
          <div className="relative">
            <a
              href="https://discord.com/invite/glowsai"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full py-2 px-[5px] bg-panel-gray-200 hover:bg-panel-gray-100 cursor-pointer block"
            >
              <DiscordIcon className="w-[22px] h-4 text-tertiary" />
            </a>
            {showDiscordTooltip && (
              <div className="absolute bottom-[calc(100%-2px)] left-1/2 mb-0.5 px-2 py-0.5 bg-btn-secondary text-btn-foreground rounded-lg text-xs whitespace-nowrap z-50 animate-wobble before:content-[''] before:absolute before:top-full before:left-1/2 before:transform before:-translate-x-1/2 before:border-2 before:border-transparent before:border-t-btn-secondary">
                Need Help?
              </div>
            )}
          </div>
          {shouldShowOnboardingGuide ? (
            <>
              <div className="h-5 border-r border-r-gray100"></div>
              <button
                type="button"
                aria-label="Onboarding guide"
                onClick={() => setOnboardingOpen(true)}
                className="relative rounded-full p-1.5 bg-panel-gray-200 hover:bg-panel-gray-100 cursor-pointer block"
                data-ai-component="onboarding-guide"
                data-ai-action="open-onboarding-guide"
                data-ai-value={onboardingUndoneCount}
              >
                <OnboardingGuideIcon className="w-5 h-5 text-tertiary" />
                {!onboardingLoading && onboardingUndoneCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-btn-secondary text-white text-[10px] leading-[10px] font-medium flex items-center justify-center">
                    {onboardingUndoneCount}
                  </span>
                )}
              </button>
            </>
          ) : null}
          <div className="h-5 border-r border-r-gray100"></div>
          <div
            className="relative flex h-8 w-8 items-center justify-center before:absolute before:left-1/2 before:top-full before:h-2 before:w-32 before:-translate-x-1/2 before:content-['']"
            data-ai-component="header-docs-menu"
            onMouseEnter={handleDocsMenuMouseEnter}
            onMouseLeave={handleDocsMenuMouseLeave}
            onFocus={handleDocsMenuMouseEnter}
            onBlur={handleDocsMenuMouseLeave}
          >
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center rounded-full bg-panel-gray-200 text-tertiary transition-colors hover:bg-panel-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/40"
              aria-label="Documentation links"
              aria-haspopup="menu"
              aria-expanded={showDocsMenu}
            >
              <DocumentationIcon className="h-5 w-5" />
            </button>
            <ul
              role="menu"
              className={[
                'absolute left-1/2 top-10 z-50 w-32 -translate-x-1/2 rounded-2xl bg-panel-white p-2 shadow-[0px_64px_240px_0px_rgba(33,51,101,0.05),0px_20px_72px_0px_rgba(33,51,101,0.03),0px_8px_32px_0px_rgba(33,51,101,0.03),0px_4px_12px_0px_rgba(33,51,101,0.02)] transition-all duration-200',
                showDocsMenu ? 'visible opacity-100' : 'invisible opacity-0',
              ].join(' ')}
            >
              {HEADER_DOC_LINKS.map(({ hrefByLanguage, label }) => {
                const href = hrefByLanguage[docLanguage]

                return (
                  <li key={href} role="none">
                    <a
                      role="menuitem"
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="t-sb block min-h-10 rounded-lg px-2 py-2.5 text-primary transition-colors hover:bg-panel-gray-100 focus-visible:bg-panel-gray-100 focus-visible:outline-none"
                      data-ai-action="open-documentation"
                      data-ai-value={href}
                    >
                      {label}
                    </a>
                  </li>
                )
              })}
            </ul>
          </div>
          <div className="h-5 border-r border-r-gray100"></div>
          <UserDropdown eduVerify={eduVerify} />
          {shouldShowOnboardingGuide ? (
            <OnboardingGuideModal
              open={onboardingOpen}
              tasks={onboardingTasks}
              loading={onboardingLoading}
              onClose={() => setOnboardingOpen(false)}
              onFollowXClick={handleFollowXClick}
              onDepositClick={() => {
                setOnboardingOpen(false)
                setExchargeModalOpen(true)
              }}
            />
          ) : null}
        </div>
      </div>
    </header>
  )
}
